/*
  NINI header - viselkedes a sections/nini-header.liquid-hez.
    <nini-header>           hoverre (es fokuszra) nyilo lenyilok / kollekcio panelek,
                            --header-height frissitese, Theme Editor blokk-kijeloles
    <nini-collection-tabs>  kollekcio tabok (desktopon hoverre is), csuszo tabvaltas,
                            termekslider nyilak
  Keszitette: Akos | branch: dev/akos | 2026-09-26
  Modositva:  Akos | branch: dev/akos | 2026-09-28 - a kollekcio tabok linkek (kattintas = navigalas; erintokepernyon elso koppintas = valtas)
  Modositva: Akos | branch: dev/akos | 2026-10-09 - kollekciogomb kijelolesekor a megfelelo menu nyitasa az Editorban
  Modositva: Akos | branch: dev/akos | 2026-10-09 - lapozonyil csak ot termek felett, elerheto iranyban
  Modositva: Akos | branch: dev/akos | 2026-10-09 - fuggoleges tablistanal fel/le billentyuk
  Modositva: Akos | branch: dev/akos | 2026-10-29 - almenuelem kivalasztasakor a hozza tartozo termekful is megnyilik
*/

(() => {
  const CLOSE_DELAY = 200;
  const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  class NiniHeader extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.nini-header__nav-item[data-panel]'));
      this.activeItem = null;
      this.closeTimer = null;

      this.items.forEach((item) => {
        const panel = this._panelFor(item);
        const link = item.querySelector('.nini-header__nav-link');
        if (!panel || !link) return;

        item.addEventListener('mouseenter', () => canHover() && this.open(item));
        item.addEventListener('mouseleave', () => canHover() && this.scheduleClose());
        link.addEventListener('focus', () => this.open(item));

        // Erintokepernyon (pl. iPad fekvo modban) az elso koppintas nyit, a masodik navigal.
        link.addEventListener('click', (event) => {
          if (!canHover() && this.activeItem !== item) {
            event.preventDefault();
            this.open(item);
          }
        });
      });

      this.querySelectorAll('.nini-header__panel').forEach((panel) => {
        panel.addEventListener('mouseenter', () => canHover() && this.cancelClose());
        panel.addEventListener('mouseleave', () => canHover() && this.scheduleClose());
      });

      this._onKeydown = (event) => {
        if (event.key === 'Escape' && this.activeItem) {
          const link = this.activeItem.querySelector('.nini-header__nav-link');
          this.close();
          link && link.focus({ preventScroll: true });
        }
      };
      this._onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      };
      this._onDocumentClick = (event) => {
        if (this.activeItem && !this.contains(event.target)) this.close();
      };

      this.addEventListener('keydown', this._onKeydown);
      this.addEventListener('focusout', this._onFocusOut);
      document.addEventListener('click', this._onDocumentClick);

      this._observeHeight();
      this._bindThemeEditor();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocumentClick);
      this.resizeObserver && this.resizeObserver.disconnect();
      if (this._editorHandlers) {
        this._editorHandlers.forEach(([name, handler]) => document.removeEventListener(name, handler));
      }
    }

    _panelFor(item) {
      return document.getElementById(item.dataset.panel);
    }

    open(item) {
      this.cancelClose();
      if (this.activeItem === item) return;
      this.close();

      const panel = this._panelFor(item);
      if (!panel) return;

      this.activeItem = item;
      panel.classList.add('is-open');
      const link = item.querySelector('.nini-header__nav-link');
      link && link.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.cancelClose();
      if (!this.activeItem) return;

      const panel = this._panelFor(this.activeItem);
      panel && panel.classList.remove('is-open');
      const link = this.activeItem.querySelector('.nini-header__nav-link');
      link && link.setAttribute('aria-expanded', 'false');
      this.activeItem = null;
    }

    scheduleClose() {
      this.cancelClose();
      this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    cancelClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    // A tema tobbi resze (mini-cart, drawerek, sticky elemek) a --header-height valtozot hasznalja.
    _observeHeight() {
      const bar = this.querySelector('.nini-header__bar');
      if (!bar) return;

      const update = () => {
        const height = bar.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
        document.documentElement.style.setProperty('--header-height-without-bottom-nav', `${height}px`);
      };

      update();
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(update);
        this.resizeObserver.observe(bar);
      }
    }

    // Theme Editorban a blokk kijelolesekor nyiljon ki a hozza tartozo panel.
    _bindThemeEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;

      const onSelect = (event) => {
        const item = this.items.find((candidate) => {
          const panel = this._panelFor(candidate);
          return panel && panel.contains(event.target);
        });
        item && this.open(item);
      };
      const onDeselect = () => this.close();

      this._editorHandlers = [
        ['shopify:block:select', onSelect],
        ['shopify:block:deselect', onDeselect],
      ];
      this._editorHandlers.forEach(([name, handler]) => document.addEventListener(name, handler));
    }
  }

  class NiniCollectionTabs extends HTMLElement {
    connectedCallback() {
      // A mobil drawer (append-body) athelyezheti az elemet; a listenerek csak egyszer kellenek.
      if (this._bound) return;
      this._bound = true;

      this.tabs = Array.from(this.querySelectorAll('.nini-collections__tab'));
      this.slides = Array.from(this.querySelectorAll('.nini-collections__slide'));
      this.track = this.querySelector('.nini-collections__track');
      this.activeIndex = 0;

      this.tabs.forEach((tab, index) => {
        // A tab link: hoverrel valtunk, kattintasra a kollekciora visz. Hover nelkul (erintokepernyo,
        // mobil menu) az elso koppintas csak valt, az aktiv tabra koppintas navigal.
        tab.addEventListener('click', (event) => {
          const hoverSwitches = this.hasAttribute('hover-tabs') && canHover();
          if (!hoverSwitches && index !== this.activeIndex) {
            event.preventDefault();
            this.select(index);
          }
        });
        tab.addEventListener('mouseenter', () => {
          if (this.hasAttribute('hover-tabs') && canHover()) this.select(index);
        });
        tab.addEventListener('keydown', (event) => this._onTabKeydown(event, index));
      });

      this.slides.forEach((slide) => this._bindScroller(slide));

      this._onBlockSelect = (event) => {
        const index = this.tabs.findIndex((tab) => tab === event.target || tab.contains(event.target));
        if (index >= 0) this.select(index);
      };
      this.addEventListener('shopify:block:select', this._onBlockSelect);
    }

    select(index) {
      if (index === this.activeIndex || !this.slides[index]) return;
      this.activeIndex = index;

      this.track.style.setProperty('--nini-active-index', index);

      this.tabs.forEach((tab, tabIndex) => {
        tab.setAttribute('aria-selected', tabIndex === index ? 'true' : 'false');
      });

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    }

    _onTabKeydown(event, index) {
      const vertical = this.querySelector('[role="tablist"]')?.getAttribute('aria-orientation') === 'vertical';
      const keys = vertical ? { ArrowDown: 1, ArrowUp: -1 } : { ArrowRight: 1, ArrowLeft: -1 };
      if (!(event.key in keys)) return;

      event.preventDefault();
      const direction = !vertical && document.dir === 'rtl' ? -keys[event.key] : keys[event.key];
      const next = (index + direction + this.tabs.length) % this.tabs.length;
      this.select(next);
      this.tabs[next].focus();
    }

    _bindScroller(slide) {
      const scroller = slide.querySelector('.nini-collections__scroller');
      const prev = slide.querySelector('.nini-collections__arrow--prev');
      const next = slide.querySelector('.nini-collections__arrow--next');
      if (!scroller || !prev || !next) return;

      const isRtl = () => getComputedStyle(scroller).direction === 'rtl';

      const update = () => {
        const max = scroller.scrollWidth - scroller.clientWidth;
        const position = Math.abs(scroller.scrollLeft);
        const canScroll = scroller.children.length > 5 && max > 1;
        prev.disabled = !canScroll || position <= 1;
        next.disabled = !canScroll || position >= max - 1;
      };

      [prev, next].forEach((button) => {
        button.addEventListener('click', () => {
          const direction = Number(button.dataset.direction) * (isRtl() ? -1 : 1);
          scroller.scrollBy({ left: direction * scroller.clientWidth, behavior: 'smooth' });
        });
      });

      scroller.addEventListener('scroll', update, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(update).observe(scroller);
      update();
    }
  }

  if (!customElements.get('nini-header')) customElements.define('nini-header', NiniHeader);
  if (!customElements.get('nini-collection-tabs')) customElements.define('nini-collection-tabs', NiniCollectionTabs);
})();
